import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded'
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined'
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag'
import { Box, ButtonBase, Chip, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useTranslate } from 'ra-core'
import { Count, FunctionField, useListContext, useRecordContext } from 'react-admin'

import AdvancedSelectInput from '../../components/AdvancedSelectInput'
import { EnumField } from '../../components/EnumIconSelectInput'
import {
  INCIDENT_REPORT_STATUS_DISCARDED,
  INCIDENT_REPORT_STATUS_REPORTED,
  INCIDENT_REPORT_STATUS_RESOLVED,
  INCIDENT_REPORT_STATUS_VALIDATED,
} from '../../config/damages'

const STATUS_VISUALS = {
  [INCIDENT_REPORT_STATUS_REPORTED]: {
    backgroundColor: '#fff4e5',
    color: '#9a3412',
    darkColor: '#fdba74',
    icon: OutlinedFlagIcon,
  },
  [INCIDENT_REPORT_STATUS_VALIDATED]: {
    backgroundColor: '#e8f1ff',
    color: '#1d4ed8',
    darkColor: '#93c5fd',
    icon: FactCheckOutlinedIcon,
  },
  [INCIDENT_REPORT_STATUS_RESOLVED]: {
    backgroundColor: '#e9f8ef',
    color: '#166534',
    darkColor: '#86efac',
    icon: CheckCircleOutlineRoundedIcon,
  },
  [INCIDENT_REPORT_STATUS_DISCARDED]: {
    backgroundColor: '#f1f5f9',
    color: '#334155',
    darkColor: '#cbd5e1',
    icon: HighlightOffRoundedIcon,
  },
}

const STATUS_CHOICES = [
  { id: INCIDENT_REPORT_STATUS_REPORTED, icon: OutlinedFlagIcon },
  { id: INCIDENT_REPORT_STATUS_VALIDATED, icon: FactCheckOutlinedIcon },
  { id: INCIDENT_REPORT_STATUS_RESOLVED, icon: CheckCircleOutlineRoundedIcon },
  { id: INCIDENT_REPORT_STATUS_DISCARDED, icon: HighlightOffRoundedIcon },
]

const STATUS_SELECTOR_ITEMS = [
  { value: null },
  { value: INCIDENT_REPORT_STATUS_REPORTED },
  { value: INCIDENT_REPORT_STATUS_VALIDATED },
  { value: INCIDENT_REPORT_STATUS_RESOLVED },
  { value: INCIDENT_REPORT_STATUS_DISCARDED },
]

export const getDamageReportStatusVisuals = (status) =>
  STATUS_VISUALS[status] || STATUS_VISUALS[INCIDENT_REPORT_STATUS_REPORTED]

export const IncidentReportStatusChoiceText = () => {
  const choice = useRecordContext()
  if (!choice) return null

  return <EnumField source="status" choices={STATUS_CHOICES} value={choice.id} icon labelSx={{ color: 'inherit' }} />
}

export const IncidentReportStatusBadge = ({ status = null }) => {
  const record = useRecordContext()
  const currentStatus = status || record?.status
  const { icon, ...badgeStyles } = getDamageReportStatusVisuals(currentStatus)

  if (!currentStatus) return null

  return (
    <Chip
      label={
        <EnumField
          source="status"
          choices={STATUS_CHOICES}
          value={currentStatus}
          labelVariant="inherit"
          labelSx={{ color: 'inherit' }}
        />
      }
      size="small"
      sx={{
        height: 31,
        borderRadius: 999,
        fontWeight: 600,
        fontSize: 13,
        boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.2)',
        '& .MuiChip-label': {
          px: 1.3,
          lineHeight: 1,
        },
        ...badgeStyles,
      }}
    />
  )
}

export const IncidentReportStatusField = ({ source = 'status' }) => (
  <FunctionField source={source} render={(record) => <IncidentReportStatusBadge status={record?.[source]} />} />
)

export const IncidentReportStatusSelectInput = (props) => (
  <AdvancedSelectInput
    source="status"
    choices={STATUS_CHOICES}
    optionText={<IncidentReportStatusChoiceText />}
    optionValue="id"
    {...props}
  />
)

export const IncidentReportStatusSelector = () => {
  const translate = useTranslate()
  const { filterValues, displayedFilters, setFilters } = useListContext()
  const selectedStatus = filterValues?.status
  const { status, ...filtersWithoutStatus } = filterValues || {}

  const toggleStatus = (nextStatus) => {
    if (selectedStatus === nextStatus) {
      setFilters(filtersWithoutStatus, displayedFilters, false)
      return
    }

    setFilters({ ...filtersWithoutStatus, status: nextStatus }, displayedFilters, false)
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      sx={{
        flexWrap: 'wrap',
        justifyContent: { xs: 'flex-start', md: 'flex-end' },
        alignItems: 'center',
        maxWidth: '100%',
      }}
    >
      {STATUS_SELECTOR_ITEMS.map(({ value }) => {
        const isAllItem = value === null
        const isSelected = isAllItem ? !selectedStatus : selectedStatus === value
        const visual = getDamageReportStatusVisuals(value)
        const StatusIcon = isAllItem ? ListAltOutlinedIcon : visual.icon || OutlinedFlagIcon
        const countFilter = isAllItem ? filtersWithoutStatus : { ...filtersWithoutStatus, status: value }
        const itemStyles = isAllItem
          ? {
              backgroundColor: '#f8fafc',
              color: '#475569',
              darkColor: '#e2e8f0',
            }
          : visual

        return (
          <ButtonBase
            key={value || 'all'}
            onClick={() => (isAllItem ? toggleStatus(selectedStatus) : toggleStatus(value))}
            sx={{
              borderRadius: 999,
              textAlign: 'left',
              display: 'block',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.9,
                minHeight: 34,
                px: 1.2,
                py: 0.55,
                borderRadius: 999,
                border: '1px solid',
                borderColor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? alpha(itemStyles.darkColor || itemStyles.color, isSelected ? 0.5 : 0.28)
                    : alpha(itemStyles.color, isSelected ? 0.3 : 0.12),
                backgroundColor: isSelected
                  ? (theme) =>
                      theme.palette.mode === 'dark'
                        ? alpha(itemStyles.darkColor || itemStyles.color, isAllItem ? 0.22 : 0.3)
                        : itemStyles.backgroundColor
                  : isAllItem
                    ? (theme) => (theme.palette.mode === 'dark' ? alpha('#fff', 0.06) : '#ffffff')
                    : (theme) =>
                        theme.palette.mode === 'dark'
                          ? alpha(itemStyles.darkColor || itemStyles.color, 0.14)
                          : alpha(itemStyles.backgroundColor, 0.45),
                color: (theme) =>
                  theme.palette.mode === 'dark' ? itemStyles.darkColor || itemStyles.color : itemStyles.color,
                boxShadow: (theme) =>
                  isSelected
                    ? `inset 0 0 0 1px ${alpha(
                        theme.palette.mode === 'dark' ? itemStyles.darkColor || itemStyles.color : itemStyles.color,
                        theme.palette.mode === 'dark' ? 0.12 : 0.08,
                      )}`
                    : 'none',
                transition: 'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
              }}
            >
              <StatusIcon sx={{ fontSize: 16, color: 'inherit', flexShrink: 0 }} />
              {isAllItem ? (
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ color: 'inherit', fontWeight: isSelected ? 700 : 600, lineHeight: 1, whiteSpace: 'nowrap' }}
                >
                  {translate('resources.incident-reports.list.statusTabs.all')}
                </Typography>
              ) : (
                <EnumField
                  source="status"
                  choices={STATUS_CHOICES}
                  value={value}
                  icon={false}
                  labelVariant="body2"
                  labelSx={{
                    color: 'inherit',
                    fontWeight: isSelected ? 700 : 600,
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                  }}
                />
              )}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 24,
                  px: 0.7,
                  py: 0.15,
                  borderRadius: 999,
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? alpha(itemStyles.darkColor || itemStyles.color, isSelected ? 0.32 : 0.18)
                      : alpha(itemStyles.color, isSelected ? 0.16 : 0.08),
                }}
              >
                <Count
                  filter={countFilter}
                  timeout={250}
                  sx={{ color: 'inherit', fontWeight: isSelected ? 700 : 600, fontSize: 12, lineHeight: 1 }}
                />
              </Box>
            </Box>
          </ButtonBase>
        )
      })}
    </Stack>
  )
}
